import { useEffect, useState } from "react";
import { PrioritySelector } from "./priority-selector";
import { CatalogProductSelector } from "./catalog-product-selector";
import { DesignLayerSelector } from "./design-layer-selector";
import { OrganizationSelector } from "./organization-selector";
import { ProductMetaForm } from "./product-meta-form";
import { TagSelector } from "./tag-selector";
import { StoreSelector } from "./store-selector";
import { VariantColorSelector } from "./variant-colors-selector";
import { AssignDesignAreas } from "./assign-design-areas";
import { sortDesignAreas } from "../../../../utils/design-areas";
import { LicenseSelector } from "./license-selector";
import { DisclosurePanel } from "./disclosure-panel";

import {
  getCatalogProductImagesBySku,
  getProofTemplatesBySku,
  createDesignRequest,
  generateStoreCollections,
  createPublishProduct,
  getCatalogProductPricesBySku,
  createDesignProductTemplate,
  getCampaign,
} from "../../../../services/axios";

import {
  ProofTemplate,
  DesignFile,
  DesignRequest,
  Collection,
  PublishProduct,
  PriceType,
  UserType,
  Member,
  OrganizationType,
  Campaign,
} from "../../../../utils/types";

import { toast } from "sonner";
import { CollectionSelector } from "./collection-selector";
import { swapDynamicWordsForListing } from "../helpers";
import { Toggle } from "../../../../components/toggle";

const priorityOptions = [
  { name: "Urgent", number: 0 },
  { name: "P1", number: 1 },
  { name: "P2", number: 2 },
  { name: "P3", number: 3 },
  { name: "P4", number: 4 },
  { name: "P5", number: 5 },
];

export function CreateDesignRequestsForm({
  cid,
  designFiles,
  catalogProducts,
}: {
  cid: string;
  designFiles: any[];
  catalogProducts: any[];
}) {
  const [campaign, setCampaign] = useState<Campaign | null>(null);
  // DESIGN
  const [autoRun, setAutoRun] = useState(false);
  const [makeTemplateRun, setMakeTemplateAutoRun] = useState(false);
  const [selectedCatalogProduct, setSelectedCatalogproduct] = useState<any[]>(
    []
  );
  const [variantColors, setVariantColors] = useState<any[]>([]);
  const [selectedVariantColors, setSelectedVariantColors] = useState<any[]>([]);
  const [parentOrganizations, setParentOrganizations] = useState<
    { label: string; value: OrganizationType }[]
  >([]);
  const [childOrganizations, setChildOrganizations] = useState<
    { label: string; value: OrganizationType }[]
  >([]);
  const [athletes, setAthletes] = useState<{ label: string; value: Member }[]>(
    []
  );
  const [configurationLayers, setConfigurationLayers] = useState<
    { label: string; value: string }[]
  >([]);
  const [selectedProofTemplates, setSelectedProofTemplates] = useState<
    ProofTemplate[]
  >([]);
  const [designAreas, setDesignAreas] = useState<
    { area: string; design: any }[]
  >([]);
  const [priority, setPriority] = useState<{ name: string; number: number }>(
    priorityOptions[0]
  );
  const [makeDesignRequest, setMakeDesignRequest] = useState(true);

  // LISTING
  const [handle, setHandle] = useState(
    "{college-full_name}_{college-default_name}-nil-merch-{organization-full_name}_{athlete-full_name}_{campaign-id}-{catalog-model}"
  );
  const [title, setTitle] = useState(
    "{college-colloquial_name} - {organization-full_name} : {athlete-full_name} - {campaign-description} {catalog-model}"
  );
  const [description, setDescription] = useState(
    "<p>Officially Licensed Merchandise for <strong>{athlete-full_name}</strong> at <strong>{college-full_name}</strong>.<br></p><p>{catalog-description}<p>"
  );
  const [sizeChart, setSizeChart] = useState("");
  const [estimatedOrderArrival, setEstimatedOrderArrival] = useState("");
  const [madeToOrderProduct, setMadeToOrderProduct] = useState("");
  const [metadescription, setMetadescription] = useState("");
  const [tags, setTags] = useState<
    { id: string; salesforce_id: string; value: string }[]
  >([]);
  const [collections, setCollections] = useState<Collection[]>([]);
  const [stores, setStores] = useState<string[]>(["athletesthread"]);
  const [makeProductRequest, setMakeProductRequest] = useState(false);

  // LICENSING
  const [licensing, setLicensing] = useState<string[]>([]);

  const [user, setUser] = useState<UserType>();

  useEffect(() => {
    async function getData() {
      if (!cid) {
        return;
      }
      const c = await getCampaign(cid);
      setCampaign(c);
    }
    getData();
  }, [cid]);

  useEffect(() => {
    const userObject = localStorage.getItem("user");
    if (userObject) {
      const unwrappedUser = JSON.parse(userObject);
      setUser(unwrappedUser);
    }
  }, []);

  useEffect(() => {
    async function getImages() {
      const colors = [];
      for (let i = 0; i < selectedCatalogProduct.length; i++) {
        const sku = selectedCatalogProduct[i].value.sku;
        const images = await getCatalogProductImagesBySku(sku);
        if (!images) {
          continue;
        }
        for (let j = 0; j < images.length; j++) {
          const image = images[j];
          colors.push(image);
        }
      }
      let uniqueColors = colors
        .filter(
          (v, i, a) =>
            a.findIndex((t) => t.color === v.color && t.hex === v.hex) === i
        )
        .filter((x) => x.color !== "undefined")
        .map(function (image) {
          return {
            label: image.color,
            value: image,
          };
        });
      setVariantColors(uniqueColors);
    }
    getImages();
  }, [selectedCatalogProduct]);

  useEffect(() => {
    async function getTemplates() {
      const templates: ProofTemplate[] = [];
      for (let i = 0; i < selectedCatalogProduct.length; i++) {
        const sku = selectedCatalogProduct[i].value.sku;
        const proofTemplates = await getProofTemplatesBySku(sku);
        if (!proofTemplates) {
          continue;
        }
        for (let j = 0; j < proofTemplates.length; j++) {
          const template = proofTemplates[j];
          templates.push(template);
        }
      }
      setSelectedProofTemplates(templates);
    }
    getTemplates();
  }, [selectedCatalogProduct]);

  useEffect(() => {
    async function getCollections() {
      const pids = parentOrganizations.map((x) => x.value.salesforce_id);
      const cids = childOrganizations.map((x) => x.value.salesforce_id);
      const aids = athletes.map((x) => x.value.salesforce_id);
      const sids = [...pids, ...cids, ...aids];
      if (!sids.length) {
        return;
      }
      console.log("sids", sids);
      const results = await generateStoreCollections({
        salesforce_ids: sids,
        stores: stores,
      });
      setCollections(results);
    }
    getCollections();
    function getTags() {
      const t = [];
      for (let i = 0; i < parentOrganizations.length; i++) {
        for (let j = 0; j < parentOrganizations[i].value.tags.length; j++) {
          t.push({
            id: `${parentOrganizations[i].value.salesforce_id}-${parentOrganizations[i].value.tags[j]}`,
            salesforce_id: parentOrganizations[i].value.salesforce_id,
            value: parentOrganizations[i].value.tags[j],
          });
        }
      }
      for (let i = 0; i < childOrganizations.length; i++) {
        for (let j = 0; j < childOrganizations[i].value.tags.length; j++) {
          t.push({
            id: `${childOrganizations[i].value.salesforce_id}-${childOrganizations[i].value.tags[j]}`,
            salesforce_id: childOrganizations[i].value.salesforce_id,
            value: childOrganizations[i].value.tags[j],
          });
        }
      }
      for (let i = 0; i < athletes.length; i++) {
        for (let j = 0; j < athletes[i].value.tags.length; j++) {
          t.push({
            id: `${athletes[i].value.salesforce_id}-${athletes[i].value.tags[j]}`,
            salesforce_id: athletes[i].value.salesforce_id,
            value: athletes[i].value.tags[j],
          });
        }
      }
      setTags(t);
    }
    getTags();
  }, [parentOrganizations, childOrganizations, athletes, stores]);

  async function generateRequests() {
    const templates = selectedProofTemplates;

    if (!templates.length) {
      return toast.error("please select product templates", {
        duration: 5000,
      });
    }

    const catalog_products = selectedCatalogProduct.map((x) => x.value);

    if (!catalog_products.length) {
      return toast.error("please select products", {
        duration: 5000,
      });
    }

    const colors = selectedVariantColors.map((x) => x.value);

    if (!colors.length) {
      return toast.error("please select product colors", {
        duration: 5000,
      });
    }

    const parent_orgs = parentOrganizations.map((x) => x.value);
    const child_orgs = childOrganizations.map((x) => x.value);
    const org_athletes = athletes.map((x) => x.value);
    const priority_value = priority.number;
    let configuration_layers = configurationLayers.map((x) => x.value);

    type RequestAthlete = (typeof org_athletes)[number];
    const configs: {
      parent: (typeof parent_orgs)[number];
      child: (typeof child_orgs)[number] | null;
      athlete: RequestAthlete | null;
      athletes?: RequestAthlete[];
    }[] = [];

    for (let p = 0; p < parent_orgs.length; p++) {
      const parent = parent_orgs[p];
      const children = child_orgs.filter((x) =>
        x.parents.includes(parent.salesforce_id)
      );
      if (!children.length) {
        configs.push({
          parent: parent,
          child: null,
          athlete: null,
        });
        continue;
      }
      for (let c = 0; c < children.length; c++) {
        const child = children[c];
        const athletes = org_athletes.filter(
          (x) => x.org_salesforce_id === child.salesforce_id
        );
        if (!athletes.length) {
          configs.push({
            parent: parent,
            child: child,
            athlete: null,
          });
          continue;
        }
        const filteredAthletesByChild = athletes.filter(
          (x) => x.org_salesforce_id === child.salesforce_id
        );
        if (
          configuration_layers.length === 0 &&
          filteredAthletesByChild.length > 0
        ) {
          // Non-configurable design (no name/number layers): build ONE shared
          // listing attributed to ALL athletes instead of one product each.
          configs.push({
            parent: parent,
            child: child,
            athlete: null,
            athletes: filteredAthletesByChild,
          });
        } else {
          // Configurable design: one product per athlete (each gets a unique
          // name/number configured into the design).
          for (let a = 0; a < filteredAthletesByChild.length; a++) {
            if (!configuration_layers.includes("members")) {
              configuration_layers.push("members");
            }
            configs.push({
              parent: parent,
              child: child,
              athlete: filteredAthletesByChild[a],
            });
          }
        }
      }
    }

    if (!configs.length) {
      return generateNonConfigurableRequests();
    }

    for (let i = 0; i < catalog_products.length; i++) {
      const product = catalog_products[i];
      const prices = await getCatalogProductPricesBySku(product.sku);

      let product_colors = colors
        .filter((x) => x.sku === product.sku)
        .map((x) => {
          return { color_name: x.color, color_hex: x.hex };
        });

      for (let c = 0; c < configs.length; c++) {
        let config = configs[c];
        // Non-configurable shared listing → config.athletes holds every athlete;
        // configurable path → the single config.athlete. One source of truth for
        // all athlete-derived fields below (athletes array, metafields, tags).
        const configAthletes = config.athletes ?? (config.athlete ? [config.athlete] : []);
        const configAthleteSfIds = configAthletes.map((a) => a.salesforce_id);
        const request_ids: string[] = [];
        const original_files: string[] = [];
        for (let a = 0; a < designAreas.length; a++) {
          const area = designAreas[a].area;
          const design = designAreas[a].design.value as DesignFile;
          if (!area) {
            continue;
          }
          const template = templates.find(
            (x: ProofTemplate) =>
              x.sku === product.sku && x.design_areas.includes(area)
          );
          if (!template) {
            continue;
          }

          const configured_athletes = configAthleteSfIds;

          let configured_organizations = config.child
            ? [config.child.salesforce_id]
            : [];

          if (!config.child) {
            configured_organizations = [config.parent.salesforce_id];
          }

          const configured_parents = config.parent
            ? [config.parent.salesforce_id]
            : [];

          let designRequest: Omit<
            DesignRequest,
            | "created_at"
            | "updated_at"
            | "id"
            | "pk"
            | "configured_data_url"
            | "request_id"
            | "request_job_url"
          > = {
            status: "start",
            campaign: cid,
            design_file_url: design.file_url,
            design_file_name: design.file_name,
            design_file_id: design.id,
            product_template_name: template.proof_template_name,
            product_template_url: template.proof_template_url,
            product_template_brand: product.brand,
            product_template_model: product.model,
            product_template_sku: product.sku,
            configured: design.configurable,
            configured_variants: product_colors,
            configured_layers: configuration_layers,
            configured_design_area: area,
            configured_athletes: configured_athletes,
            configured_organizations: configured_organizations,
            parent_organizations: configured_parents,
            request_priority: priority_value,
          };

          if (!makeDesignRequest) {
            continue;
          }

          const request_id = await createDesignRequest(designRequest);
          if (!request_id) {
            toast.error("something went wrong, missing request id", {
              duration: 5000,
            });
            continue;
          } else {
            toast.success(`Design ${request_id} Created!`, { duration: 5000 });
            request_ids.push(request_id);
          }
          original_files.push(design.id);
        }

        const fHandle = swapDynamicWordsForListing({
          str: handle,
          campaign: campaign,
          athlete: config.athlete,
          college: config.parent,
          organization: config.child,
          catalogProduct: product,
          prices: prices,
        })
          .split(" ")
          .join("-")
          .toLowerCase();

        const fTitle = swapDynamicWordsForListing({
          str: title,
          campaign: campaign,
          athlete: config.athlete,
          college: config.parent,
          organization: config.child,
          catalogProduct: product,
          prices: prices,
        });
        const fDescription = swapDynamicWordsForListing({
          str: description,
          campaign: campaign,
          athlete: config.athlete,
          college: config.parent,
          organization: config.child,
          catalogProduct: product,
          prices: prices,
        });
        const mDescription = removeHtml(fDescription);

        let athleteCollections = collections.filter((x) =>
          configAthleteSfIds.includes(x.salesforce_id)
        );

        let orgCollections = collections.filter(
          (x) => x.salesforce_id === config.child?.salesforce_id
        );

        const parentCollections = collections.filter(
          (x) => x.salesforce_id === config.parent.salesforce_id
        );

        // Merge all athletes' + org + parent collections, de-duplicated (a shared
        // listing can pull the same collection via multiple athletes/orgs).
        const seenCollectionKeys = new Set<string>();
        const filteredCollections = [
          ...athleteCollections,
          ...orgCollections,
          ...parentCollections,
        ]
          .filter((collection) => {
            const key = `${collection.salesforce_id}-${collection.shopify_id}`;
            if (seenCollectionKeys.has(key)) {
              return false;
            }
            seenCollectionKeys.add(key);
            return true;
          })
          .map(function (collection) {
            return {
              ...collection,
              shopify_id: `${collection.shopify_id}`,
            };
          });

        const filteredSizes = prices.map((x: PriceType) => `${x.size}`);

        if (!request_ids.length) {
          continue;
        }

        let metafields = [];

        if (cid) {
          metafields.push({
            namespace: "campaign",
            key: "campaignId",
            value: cid,
            type: "single_line_text_field",
          });
        }

        if (configAthletes.length > 0) {
          metafields.push({
            namespace: "athletes",
            key: "salesforceids",
            value: JSON.stringify(configAthleteSfIds),
            type: "list.single_line_text_field",
          });
          metafields.push({
            namespace: "athletes",
            key: "names",
            value: JSON.stringify(configAthletes.map((a) => a.full_name)),
            type: "list.single_line_text_field",
          });
          metafields.push({
            namespace: "athletes",
            key: "sports",
            value: JSON.stringify([
              ...new Set(
                configAthletes.flatMap((a) =>
                  a.sports ? a.sports.split(",") : []
                )
              ),
            ]),
            type: "list.single_line_text_field",
          });
          metafields.push({
            namespace: "athletes",
            key: "college",
            value: JSON.stringify([config.parent.full_name]),
            type: "list.single_line_text_field",
          });
        }

        if (config.child) {
          metafields.push({
            namespace: "organizations",
            key: "salesforceids",
            value: JSON.stringify([config.child.salesforce_id]),
            type: "list.single_line_text_field",
          });
          metafields.push({
            namespace: "organizations",
            key: "names",
            value: JSON.stringify([config.child.full_name]),
            type: "list.single_line_text_field",
          });
        }

        if (config.parent && config.child && config.athlete) {
          const affinity_organization_id =
            config.child.affinity_organization_id;
          const affinity_product_category_id =
            product.affinity_product_category_id;
          let val = `${cid}-${product.sku}-${config.athlete.org_salesforce_id}`;
          if (
            affinity_product_category_id &&
            affinity_product_category_id !== ""
          ) {
            val += `-${affinity_product_category_id}`;
          }
          if (affinity_organization_id && affinity_organization_id !== "") {
            val += `-${affinity_organization_id}`;
          }
          metafields.push({
            namespace: "affinity",
            key: "upi",
            value: JSON.stringify([val]),
            type: "list.single_line_text_field",
          });
        }

        if (config.parent && config.child && !config.athlete) {
          const affinity_organization_id =
            config.child.affinity_organization_id;
          const affinity_product_category_id =
            product.affinity_product_category_id;
          let val = `${cid}-${product.sku}`;
          if (
            affinity_product_category_id &&
            affinity_product_category_id !== ""
          ) {
            val += `-${affinity_product_category_id}`;
          }
          if (affinity_organization_id && affinity_organization_id !== "") {
            val += `-${affinity_organization_id}`;
          }
          metafields.push({
            namespace: "affinity",
            key: "upi",
            value: JSON.stringify([val]),
            type: "list.single_line_text_field",
          });
        }

        if (config.parent && !config.child && !config.athlete) {
          const affinity_organization_id =
            config.parent.affinity_organization_id;

          let val = `${cid}-${product.sku}`;
          if (affinity_organization_id && affinity_organization_id !== "") {
            val += `-${affinity_organization_id}`;
          }
          metafields.push({
            namespace: "affinity",
            key: "upi",
            value: JSON.stringify([val]),
            type: "list.single_line_text_field",
          });
        }

        const configured_athletes = configAthleteSfIds;

        let configured_organizations = config.child
          ? [config.child.salesforce_id]
          : [];

        if (!config.child) {
          configured_organizations = [config.parent.salesforce_id];
        }

        const filteredTags = [
          ...new Set(
            tags
              .filter(
                (x) =>
                  x.salesforce_id === config.child?.salesforce_id ||
                  x.salesforce_id === config.parent.salesforce_id ||
                  configAthleteSfIds.includes(x.salesforce_id)
              )
              .map((x) => x.value)
          ),
        ];

        let listingRequest: Omit<
          PublishProduct,
          | "created_at"
          | "updated_at"
          | "id"
          | "shopify_admin_url"
          | "shopify_store_url"
        > = {
          campaign: cid,
          status: "pending designs",
          stores: stores,
          variants: [],
          design_templates: request_ids,
          vendor: product.default_vendor,
          sku: product.sku,
          row: "0000",
          handle: fHandle,
          title: fTitle,
          body_html: fDescription,
          product_type: product.product_type,
          colors: product_colors.map((x) => x.color_name),
          tags: filteredTags,
          sizes: filteredSizes,
          athletes: configured_athletes,
          organizations: configured_organizations,
          licensing: licensing,
          shopify_collections: filteredCollections,
          affinity_product_category_id: product.affinity_product_category_id,
          metafields_global_title_tag: fTitle,
          metafields_global_description_tag: mDescription,
          metafields: metafields,
          author: user ? user.id : "",
          original_files: original_files,
        };

        if (!makeProductRequest) {
          continue;
        }

        const product_id = await createPublishProduct(listingRequest);
        if (!product_id) {
          toast.error("something went wrong, missing product id", {
            duration: 5000,
          });
        } else {
          toast.success(`Product ${product_id} Created!`, { duration: 5000 });
        }
      }
    }
    return;
  }

  async function constructDesignProductTemplates() {
    try {
      const templates = selectedProofTemplates;
      const areas = designAreas.map((x) => x.area);
      const catalog_products = selectedCatalogProduct.map((x) => x.value);
      const colors = selectedVariantColors.map((x) => x.value);
      const parent_orgs = parentOrganizations.map((x) => x.value);
      const child_orgs = childOrganizations.map((x) => x.value);
      let configuration_layers = configurationLayers.map((x) => x.value);

      const configs = [];

      for (let p = 0; p < parent_orgs.length; p++) {
        const parent = parent_orgs[p];
        const children = child_orgs.filter((x) =>
          x.parents.includes(parent.salesforce_id)
        );
        if (!children.length) {
          configs.push({
            parent: parent,
            child: null,
            athlete: null,
          });
          continue;
        }
        for (let c = 0; c < children.length; c++) {
          const child = children[c];
          configs.push({
            parent: parent,
            child: child,
            athlete: null,
          });
        }
      }

      for (let i = 0; i < catalog_products.length; i++) {
        const product = catalog_products[i];
        for (let c = 0; c < configs.length; c++) {
          let config = configs[c];

          let template_length = 0;

          for (let i = 0; i < templates.length; i++) {
            const thisTemplate = templates[i];
            if (thisTemplate.sku !== product.sku) {
              continue;
            }
            const design_areas = thisTemplate.design_areas;
            for (let j = 0; j < design_areas.length; j++) {
              const design_area = design_areas[j];
              if (!areas.includes(design_area)) {
                continue;
              }
              template_length += 1;
            }
          }

          for (let a = 0; a < designAreas.length; a++) {
            const area = designAreas[a].area;
            const design = designAreas[a].design.value as DesignFile;
            if (!area) {
              continue;
            }
            const template = templates.find(
              (x: ProofTemplate) =>
                x.sku === product.sku && x.design_areas.includes(area)
            );

            if (!template) {
              continue;
            }

            let organizationId = config.child
              ? config.child.salesforce_id
              : config.parent
              ? config.parent.salesforce_id
              : "";
            let org_salesforce_ids = config.child
              ? [config.child.salesforce_id]
              : config.parent
              ? [config.parent.salesforce_id]
              : [""];

            const collegeNames = config.parent ? [config.parent.full_name] : [];
            const orgNames = config.child
              ? [config.child.full_name]
              : config.parent.full_name
              ? [config.parent.full_name]
              : [];

            const filteredTags = tags
              .filter(
                (x) =>
                  x.salesforce_id === config.child?.salesforce_id ||
                  x.salesforce_id === config.parent?.salesforce_id
              )
              .map((x) => x.value);

            const variants = colors.map((x) => x.color);

            const currentTime = new Date().getTime().toString();
            const designProductTemplate = {
              id: `${cid}-${currentTime}-${c}`,
              created_at: currentTime,
              updated_at: currentTime,
              handle: handle,
              title: title,
              body_html: description,
              size_chart: sizeChart,
              description: metadescription,
              estimated_order_arrival: estimatedOrderArrival,
              made_to_order_product: madeToOrderProduct,
              campaign: cid,
              organization: organizationId,
              file: design.id,
              sku: product.sku,
              variants: variants,
              layers: configuration_layers,
              design_area: area,
              stores: stores,
              licensing: licensing,
              college_names: collegeNames,
              org_names: orgNames,
              org_salesforce_ids: org_salesforce_ids,
              tags: filteredTags,
              upis: [],
              auto_run: autoRun,
              template_count: template_length,
              disabled: false,
            };
            console.log("designProductTemplate", designProductTemplate);
            const req = await createDesignProductTemplate(
              designProductTemplate,
              template_length
            );
            if (!req) {
              toast.error(
                "something went wrong, missing designed product template id",
                { duration: 5000 }
              );
              continue;
            }
            toast.success("posted design product template", { duration: 5000 });
          }
        }
      }
    } catch (e) {
      console.log("e", e);
    }
  }

  async function generateNonConfigurableRequests() {
    const templates = selectedProofTemplates;
    const catalog_products = selectedCatalogProduct.map((x) => x.value);
    const colors = selectedVariantColors.map((x) => x.value);
    const priority_value = priority.number;

    for (let i = 0; i < catalog_products.length; i++) {
      const product = catalog_products[i];
      const prices = await getCatalogProductPricesBySku(product.sku);

      let product_colors = colors
        .filter((x) => x.sku === product.sku)
        .map((x) => {
          return { color_name: x.color, color_hex: x.hex };
        });

      const request_ids: string[] = [];
      const original_files: string[] = [];
      for (let a = 0; a < designAreas.length; a++) {
        const area = designAreas[a].area;
        const design = designAreas[a].design.value as DesignFile;
        if (!area) {
          continue;
        }
        const template = templates.find(
          (x: ProofTemplate) =>
            x.sku === product.sku && x.design_areas.includes(area)
        );
        if (!template) {
          continue;
        }

        let designRequest: Omit<
          DesignRequest,
          | "created_at"
          | "updated_at"
          | "id"
          | "pk"
          | "configured_data_url"
          | "request_id"
          | "request_job_url"
        > = {
          status: "start",
          campaign: cid,
          design_file_url: design.file_url,
          design_file_name: design.original_file_name,
          design_file_id: design.file_name,
          product_template_name: template.proof_template_name,
          product_template_url: template.proof_template_url,
          product_template_brand: product.brand,
          product_template_model: product.model,
          product_template_sku: product.sku,
          configured: design.configurable,
          configured_variants: product_colors,
          configured_layers: [],
          configured_design_area: area,
          configured_athletes: [],
          configured_organizations: [],
          parent_organizations: [],
          request_priority: priority_value,
        };

        if (!makeDesignRequest) {
          continue;
        }

        const request_id = await createDesignRequest(designRequest);
        if (!request_id) {
          toast.error("something went wrong, missing request id", {
            duration: 5000,
          });
          continue;
        } else {
          toast.success(`Design ${request_id} Created!`, { duration: 5000 });
          request_ids.push(request_id);
        }
        original_files.push(design.id);
      }

      const fHandle = swapDynamicWordsForListing({
        str: handle,
        campaign: campaign,
        athlete: null,
        college: null,
        organization: null,
        catalogProduct: product,
        prices: prices,
      })
        .split(" ")
        .join("-")
        .toLowerCase();

      const fTitle = swapDynamicWordsForListing({
        str: title,
        campaign: campaign,
        athlete: null,
        college: null,
        organization: null,
        catalogProduct: product,
        prices: prices,
      });
      const fDescription = swapDynamicWordsForListing({
        str: description,
        campaign: campaign,
        athlete: null,
        college: null,
        organization: null,
        catalogProduct: product,
        prices: prices,
      });
      const mDescription = removeHtml(fDescription);

      const filteredSizes = [
        ...new Set(prices.map((x: PriceType) => `${x.size}`)),
      ] as string[];

      if (!request_ids.length) {
        continue;
      }

      let metafields = [];

      if (cid) {
        metafields.push({
          namespace: "campaign",
          key: "campaignId",
          value: cid,
          type: "single_line_text_field",
        });
      }

      let listingRequest: Omit<
        PublishProduct,
        | "created_at"
        | "updated_at"
        | "id"
        | "shopify_admin_url"
        | "shopify_store_url"
      > = {
        campaign: cid,
        status: "pending designs",
        stores: stores,
        variants: [],
        design_templates: request_ids,
        vendor: product.default_vendor,
        sku: product.sku,
        row: "0000",
        handle: fHandle,
        title: fTitle,
        body_html: fDescription,
        product_type: product.product_type,
        colors: product_colors.map((x) => x.color_name),
        tags: [],
        sizes: filteredSizes,
        athletes: [],
        organizations: [],
        licensing: licensing,
        shopify_collections: [],
        affinity_product_category_id: product.affinity_product_category_id,
        metafields_global_title_tag: fTitle,
        metafields_global_description_tag: mDescription,
        metafields: metafields,
        author: user ? user.id : "",
        original_files: original_files,
      };

      if (!makeProductRequest) {
        continue;
      }

      const product_id = await createPublishProduct(listingRequest);
      if (!product_id) {
        toast.error("something went wrong, missing product id", {
          duration: 5000,
        });
      } else {
        toast.success(`Product ${product_id} Created!`, { duration: 5000 });
      }
    }
    return;
  }

  async function handleGenerateRequests() {
    await generateRequests();
    if (!makeTemplateRun) {
      return;
    }
    await constructDesignProductTemplates();
  }

  function removeHtml(text: string): string {
    const tagRegex = /<[^>]*>/g;
    return text.replace(tagRegex, "");
  }

  function handleSetDesignArea(designArea: any) {
    if (!designArea.design) {
      const removed = designAreas.filter((x) => x.area !== designArea.area);
      setDesignAreas([...removed]);
      return;
    }
    const found = designAreas.find((x) => x.area === designArea.area);
    if (!found) {
      setDesignAreas([...designAreas, designArea]);
      return;
    }
    const replaced = designAreas.filter((x) => x.area !== found.area);
    setDesignAreas([...replaced, designArea]);
    return;
  }

  const files = designFiles.map((x) => {
    return { label: x.original_file_name, value: x };
  });

  const areas = sortDesignAreas([
    ...new Set(selectedProofTemplates.map((x) => x.design_areas).flat()),
  ]);

  function handleSelectedStores(store: string) {
    if (stores.includes(store)) {
      const filtered = stores.filter((x) => x !== store);
      setStores(filtered);
    } else {
      setStores([...stores, store]);
    }
  }

  function handleSelectedLicensing(licensor: string) {
    if (licensing.includes(licensor)) {
      const filtered = licensing.filter((x) => x !== licensor);
      setLicensing(filtered);
    } else {
      setLicensing([...licensing, licensor]);
    }
  }

  function handleRemoveCollection(collection_id: string) {
    const filtered = collections.filter((x) => x.id !== collection_id);
    setCollections(filtered);
  }

  function handleRemoveTag(tag: { id: string; value: string }) {
    const filtered = tags.filter((x) => x.id !== tag.id);
    setTags([...filtered]);
  }

  function handleSetAutoRun(auto: boolean) {
    setAutoRun(auto);
    setMakeDesignRequest(false);
    setMakeProductRequest(false);
  }

  return (
    <div className="w-full p-8 m-4 border rounded">
      <div className="flex-1 min-w-0 w-full">
        <h4 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
          Design & Listing Request
        </h4>
      </div>

      <CatalogProductSelector
        catalogProducts={catalogProducts}
        selectedCatalogProduct={selectedCatalogProduct}
        handleSelectedCatalogProducts={setSelectedCatalogproduct}
      />
      <VariantColorSelector
        variantColors={variantColors}
        selectedVariantColors={selectedVariantColors}
        setSelectedVariantColors={setSelectedVariantColors}
        selectAllVariantColors={() => setSelectedVariantColors(variantColors)}
      />

      <DesignLayerSelector
        configurationLayers={configurationLayers}
        setConfigurationLayers={setConfigurationLayers}
      />
      <AssignDesignAreas
        areas={areas}
        designOptions={files}
        selectDesign={(e) => handleSetDesignArea(e)}
      />

      <OrganizationSelector
        setSelectedParentOrganizations={setParentOrganizations}
        selectedParentOrganizations={parentOrganizations}
        setSelectedChildOrganizations={setChildOrganizations}
        selectedChildOrganizations={childOrganizations}
        selectedMemberOrganizations={athletes}
        setSelectedMemberOrganizations={setAthletes}
      />

      <DisclosurePanel label={"Listing"}>
        <ProductMetaForm
          handle={handle}
          title={title}
          description={description}
          sizeChart={sizeChart}
          metadescription={metadescription}
          madeToOrderProduct={madeToOrderProduct}
          estimatedOrderArrival={estimatedOrderArrival}
          setMetadescription={setMetadescription}
          setMadeToOrderProduct={setMadeToOrderProduct}
          setEstimatedOrderArrival={setEstimatedOrderArrival}
          setHandle={setHandle}
          setTitle={setTitle}
          setDescription={setDescription}
          setSizeChart={setSizeChart}
        />
      </DisclosurePanel>

      <DisclosurePanel label={"Tags"}>
        <TagSelector
          tags={tags}
          setTags={setTags}
          removeTag={handleRemoveTag}
        />
      </DisclosurePanel>

      <DisclosurePanel label={"Collections"}>
        <CollectionSelector
          collections={collections}
          removeCollection={handleRemoveCollection}
        />
      </DisclosurePanel>

      <DisclosurePanel label={"Stores"}>
        <StoreSelector
          selectedStores={stores}
          setCollections={handleSelectedStores}
        />
      </DisclosurePanel>

      <DisclosurePanel label={"Licensing"}>
        <LicenseSelector
          licensing={licensing}
          setLicensing={handleSelectedLicensing}
        />
      </DisclosurePanel>

      <DisclosurePanel label={"Design Product Template"}>
        <div>
          <div className="w-full flex flex-row items-center space-x-4 h-12 p-2">
            <Toggle
              onoff={makeTemplateRun}
              onChange={() => setMakeTemplateAutoRun(!makeTemplateRun)}
            />
            <div className="ml-3 text-sm">
              <label htmlFor="template" className="font-medium text-gray-700">
                Make Template
              </label>
            </div>
          </div>
          <div className="w-full flex flex-row items-center space-x-4 h-12 p-2">
            <Toggle
              onoff={autoRun}
              onChange={() => handleSetAutoRun(!autoRun)}
            />
            <div className="ml-3 text-sm">
              <label htmlFor="autorun" className="font-medium text-gray-700">
                Auto Run
              </label>
            </div>
          </div>
        </div>
      </DisclosurePanel>

      <div className="flex justify-end items-center space-x-4">
        <div className="flex justify-center items-center">
          {autoRun && (
            <p>
              You may not execute design and product requests at the same time
              you auto run a new template
            </p>
          )}
          {!autoRun && (
            <div className="flex justify-between items-center">
              <div className="w-full flex flex-row items-center space-x-4 h-12 p-2">
                <Toggle
                  onoff={makeDesignRequest}
                  onChange={() => setMakeDesignRequest(!makeDesignRequest)}
                />
                <div className="ml-3 text-sm">
                  <label
                    htmlFor="template"
                    className="font-medium text-gray-700"
                  >
                    Make Design Request
                  </label>
                </div>
              </div>
              <div className="w-full flex flex-row items-center space-x-4 h-12 p-2">
                <Toggle
                  onoff={makeProductRequest}
                  onChange={() => setMakeProductRequest(!makeProductRequest)}
                />
                <div className="ml-3 text-sm">
                  <label
                    htmlFor="template"
                    className="font-medium text-gray-700"
                  >
                    Make Product Request
                  </label>
                </div>
              </div>
            </div>
          )}
        </div>
        <div className="flex justify-center items-center">
          <PrioritySelector
            options={priorityOptions}
            priority={priority}
            setPriority={setPriority}
          />
        </div>
        <div className="flex justify-center items-center">
          <button
            onClick={() => handleGenerateRequests()}
            className="bg-black px-4 py-2 text-white my-4"
          >
            Submit
          </button>
        </div>
      </div>
    </div>
  );
}
